/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { IPoolLike } from './IPoolLike';
import { Container, DisplayObject } from 'pixi.js-legacy';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { GenericPool } from './GenericPool';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { ILoggerService } from '../../../../../../../libraries/logging';
import { LoggerServiceFactory } from '../../../common/logging/LoggerServiceFactory';
/**
 * ValuePool
 * A special generic object pool that creates separate pools for different string/number values
 * and automatically sets a specified property on retrieved objects to match their pool value.
 * This is useful for text objects where you want to cache objects based on their content
 * and automatically update their display value when retrieved. Unlike a standard object pool that
 * manages a single type of object, ValuePool maintains multiple sub-pools, each keyed by a specific
 * value, and ensures the retrieved object's specified property matches that value.
 * This pool is fundamentally different from the existing `GenericPool` because this pool caches objects based on the
 * content defined on a cached object, whereas a GenericPool caches based on the type, without regards to content.
 * You should use this object when you have a lot of STATIC data that has well defined bounds.
 * @example
 * ```
 * // Create a ValuePool for HighResolutionText objects
 * const textPool = ValuePool.createTextValuePool(
 *   () => new HighResolutionText("", { fontSize: 12, fill: 0x000000 }),
 *   myContainer
 * );
 *
 * // Grab text objects - the .text property is automatically set
 * const mondayText = textPool.grab(`Monday`);     // mondayText.text = `Monday`
 * const tuesdayText = textPool.grab(`Tuesday`);   // tuesdayText.text = `Tuesday`
 * const dayText = textPool.grab(15);              // dayText.text = `15`
 *
 * // Reset all sub-pools to make objects available for reuse
 * textPool.reset();
 *
 * // Clean up all sub-pools and their objects
 * textPool.destroy();
 *
 * // Example with custom key for different object types
 * const labelPool = new ValuePool<CustomLabel>(
 *   () => new CustomLabel(),
 *   container,
 *   `label`  // Will set obj.label = value
 * );
 * ```
 */
export class ValuePool<T extends DisplayObject> implements IPoolLike<T> {
	/**
	 * Factory method for creating a ValuePool specifically for HighResolutionText objects.
	 * @param creationFn Function that creates new HighResolutionText instances (typically with empty text)
	 * @param container PIXI Container where text objects will be added
	 * @param key The property name to set on retrieved objects (defaults to 'text')
	 * @returns A new ValuePool configured for HighResolutionText objects
	 */
	public static createTextValuePool(
		creationFn: () => HighResolutionText,
		container: Container,
		key: string = `text`
	): ValuePool<HighResolutionText> {
		return new ValuePool<HighResolutionText>(creationFn, container, key);
	}
	/**
	 * Map storing individual GenericPool instances, keyed by string values
	 * @protected
	 */
	protected _pools: Map<string, GenericPool<T>> = new Map<string, GenericPool<T>>();
	/**
	 * A data structure like this results in a boundless upper limit for cache expansion. So if a developer is not careful,
	 * the cache of pools can grow to an infinite size (requiring an infinite amount of memory).
	 * So this number explicitly sets a cap on how large the cache can grow
	 * The value 999 was chosen arbitrarily because 999/9999 are common damage limits in RPGs :D
	 * @private
	 */
	private _maximumCachedPools: number = 999;
	/**
	 * Logger instance for when the pool becomes obnoxiously large
	 * @private
	 */
	private _logger: ILoggerService = LoggerServiceFactory.create('ValuePool');
	/**
	 * Creates a new ValuePool instance
	 * @param _creationFn Function used to create new instances of type T for each sub-pool
	 * @param _container PIXI Container where objects will be managed
	 * @param _key The property name that will be set on retrieved objects to match their pool value
	 */
	constructor(protected _creationFn: () => T, protected _container: Container, private _key: string) {}
	/**
	 * Retrieves an object from the sub-pool associated with the given value.
	 * If no sub-pool exists for this value, creates a new one.
	 * @param value The string or number value that identifies which sub-pool to use
	 * @returns An object of type T from the appropriate sub-pool with its key property set to the cached value
	 * @throws Error if the maximum number of cached pools would be exceeded
	 */
	public grab(value: string | number): T {
		// Prevent unbounded growth of the cache
		if (this._pools.size >= this._maximumCachedPools) {
			this._logger.warn(`ValuePool: Cached pool exceeds the maximum size. Purging pool and regenerating.`);
			this.reset();
			this.destroy();
		}

		const cachedValue: string = value.toString();
		if (!this._pools.has(cachedValue)) {
			// Create new sub-pool for this value, passing the container to GenericPool
			const newPool: GenericPool<T> = new GenericPool<T>(this._creationFn, this._container);
			this._pools.set(cachedValue, newPool);
		}

		const cachedPool: GenericPool<T> = this._pools.get(cachedValue);
		const cachedObject: T = cachedPool.grab();
		// We explicitly set the watched value to the cachedValue to prevent us from having multiple creation functions
		// or forcing an API consumer to manage the text values, which could pollute the pool.
		cachedObject[this._key] = cachedValue;

		return cachedObject;
	}
	/**
	 * Resets all sub-pools, making their objects available for reuse.
	 * This should be called when you want to return all objects to their respective pools and mark as available for use
	 */
	public reset(): void {
		this._pools.forEach((pool: GenericPool<T>) => pool.reset());
	}
	/**
	 * Destroys all sub-pools and their contained objects.
	 * This performs cleanup of all PIXI resources across all value-specific pools.
	 * @param options Optional destruction options passed to each object's destroy method
	 */
	public destroy(options?: IDestroyOptions): void {
		this._pools.forEach((pool: GenericPool<T>) => pool.destroy(options));
		this._pools.clear();
	}
	/**
	 * Hides all objects in all sub-pools by calling hide() on each pool.
	 * The specific hiding behavior depends on the underlying pool implementation.
	 */
	public hide(): void {
		this._pools.forEach((pool: GenericPool<T>) => pool.hide());
	}
	/**
	 * Shows all objects in all sub-pools by calling show() on each pool.
	 * The specific showing behavior depends on the underlying pool implementation.
	 */
	public show(): void {
		this._pools.forEach((pool: GenericPool<T>) => pool.show());
	}
}
